/**
 * The path a language switch navigates to.
 *
 * Its own module, and not part of `i18n.ts`, so it stays unit-testable: `i18n.ts` reaches the router
 * (and through it the whole route tree, and through that the monolith's SST resources), which no
 * unit test can import.
 */
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { translateChildSegments, translateSlug, type SlugContext } from './slug-rewrites';

/**
 * Which slug map the matched route belongs to, read off the route ids rather than the host: the id is
 * what the rewrite already resolved the host to, so the switcher cannot disagree with it.
 *
 * This replaces an earlier boolean, "does this group localize its slugs at all", which existed because
 * `translateSlug` read one map merged across every context. Merging is lossy — `about-us` is both the
 * redesign's own `en` slug and www's `en` slug for `sobre-nosotros` — so switching to Catalan on the
 * redesign's `/es/sobre-nosotros` used to push `/ca/sobre-nosaltres`, a path only www served. Naming
 * the context is the real fix, and it lets the redesign localize its own slugs too.
 */
function slugContextOf(router: { state: { matches: ReadonlyArray<{ routeId: string }> } }): SlugContext {
	const inGroup = (group: string) =>
		router.state.matches.some(({ routeId }) => routeId === `/${group}` || routeId.startsWith(`/${group}/`));
	return inGroup('new') ? 'new' : inGroup('www') ? 'www' : 'portal';
}

/**
 * Build the path for a given locale, translating slugs as needed.
 * Pure function — no side effects.
 */
export function buildLanguagePath(
	lang: PortalLocale,
	pathname: string,
	search: string,
	currentLocale: string,
	portalId: string,
	router: { options: { context?: unknown }; state: { matches: ReadonlyArray<{ routeId: string }> } },
	currentCampaignId?: string,
): string {
	const segments = pathname.split('/').filter(Boolean);
	const isLocaleFirst = (portalLocales as readonly string[]).includes(segments[0] ?? '');
	const restSegments = isLocaleFirst ? segments.slice(1) : segments;

	let newCampaignSlug: string | undefined;
	if (currentCampaignId) {
		const qc = (router.options.context as { queryClient?: { getQueryData(key: unknown[]): unknown } } | undefined)
			?.queryClient;
		if (qc) {
			const cached = qc.getQueryData([portalId, currentLocale, 'campaigns', currentCampaignId]) as
				| { slugsByLocale?: Record<string, string>; id?: string }
				| undefined;
			newCampaignSlug = cached?.slugsByLocale?.[lang] ?? cached?.id ?? currentCampaignId;
		}
	}

	const context = slugContextOf(router);
	const newLegalSlug =
		!currentCampaignId && restSegments[0] ? translateSlug(restSegments[0], lang, context) : undefined;
	// A page that owns the segments below it localizes those too, or switching language on
	// `/es/contacto/ong/consulta` keeps the Spanish words in a French URL — a path the rewrite would
	// then not resolve.
	const newChildSegments =
		newLegalSlug !== undefined && restSegments[0]
			? translateChildSegments(restSegments[0], restSegments.slice(1), lang, context)
			: [];

	const newRestSegments =
		newCampaignSlug !== undefined
			? [newCampaignSlug, ...restSegments.slice(1)]
			: newLegalSlug !== undefined
				? [newLegalSlug, ...newChildSegments]
				: restSegments;

	return '/' + [lang, ...newRestSegments].join('/') + search;
}
